/**
 * Level "windows" — the time-of-day range during which a level is
 * actually running cross-country. Derived from accepted entries'
 * ride times with a fixed pad on either end.
 *
 * Used to compute shift↔level coverage. A volunteer's XC-Jump-Judge
 * shift covers a level only when it contains the level's full window.
 * The result drives assignment eligibility and packet contents.
 */
import { parseRideTime } from '../domain/parsing.js';
/** Pad on each side of the min/max ride-time, in minutes. */
export const LEVEL_WINDOW_PADDING_MIN = 15;
/**
 * Compute a level's window from its entries' XC ride times. Returns
 * `null` when no entry has a parseable ride time.
 */
export function levelWindowFromEntries(entries, options = {}) {
    return analyzeLevelWindowFromEntries(entries, options).window;
}
export function analyzeLevelWindowFromEntries(entries, options = {}) {
    const padding = options.paddingMin ?? LEVEL_WINDOW_PADDING_MIN;
    const statuses = options.includeStatuses ?? ['Accepted'];
    const times = [];
    for (const e of entries) {
        if (!statuses.includes(e.status))
            continue;
        const min = extractMinutesOfDay(e.xcRideTime, e.xcRideTimeRaw);
        if (min != null)
            times.push(min);
    }
    if (times.length === 0)
        return { window: null, excludedAmPmOutliers: [] };
    const excludedAmPmOutliers = likelyAmPmOutliers(times);
    const includedTimes = times.filter((time) => !excludedAmPmOutliers.includes(time));
    let min = includedTimes[0];
    let max = includedTimes[0];
    for (const t of includedTimes) {
        if (t < min)
            min = t;
        if (t > max)
            max = t;
    }
    return {
        window: {
            startMin: Math.max(0, min - padding),
            endMin: Math.min(24 * 60, max + padding),
        },
        excludedAmPmOutliers,
    };
}
function likelyAmPmOutliers(times) {
    if (times.length < 3)
        return [];
    return times.filter((time, index) => {
        const others = times.filter((_, otherIndex) => otherIndex !== index);
        const nearestActual = Math.min(...others.map((other) => Math.abs(time - other)));
        const shifted = [time - 12 * 60, time + 12 * 60].filter((candidate) => candidate >= 0 && candidate < 24 * 60);
        const nearestShifted = Math.min(...shifted.flatMap((candidate) => others.map((other) => Math.abs(candidate - other))));
        return nearestActual >= 6 * 60 && nearestShifted <= 60;
    });
}
export function shiftCoversLevel(shift, window) {
    if (shift.allDay)
        return true;
    if (shift.shiftStartMin == null || shift.shiftEndMin == null)
        return false;
    return shift.shiftStartMin <= window.startMin && shift.shiftEndMin >= window.endMin;
}
/**
 * Whether *any* of the supplied shifts cover the window.
 */
export function shiftsCoverLevel(shifts, window) {
    for (const s of shifts) {
        if (shiftCoversLevel(s, window))
            return true;
    }
    return false;
}
function extractMinutesOfDay(iso, raw) {
    const isoMatch = iso ? /T(\d{2}):(\d{2})/.exec(iso) : null;
    if (isoMatch) {
        const h = Number(isoMatch[1]);
        const m = Number(isoMatch[2]);
        if (Number.isFinite(h) && Number.isFinite(m))
            return h * 60 + m;
    }
    const parsed = parseRideTime(raw ?? null);
    return parsed ? parsed.timeMin : null;
}
//# sourceMappingURL=levelWindows.js.map